import { CellFormatHelper } from './CellFormatHelper';

// Constants for styling
const DEFAULT_FONT = { name: 'Arial', size: 10, bold: false };
const HEADER_FONT = { name: 'Arial', size: 11, bold: true, alignment: { vertical: 'middle', horizontal: 'center', wrapText: false }, color: { argb: 'FF000000' } }; 
const FIRST_COL_FONT = { name: 'Arial', size: 10, bold: true };
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC3F938' } }; // Green fill
const BORDER_STYLE = { style: 'thin', color: { argb: 'FF466300' } }; // Thin dark green border
const ALL_BORDERS = {
    top: { style: 'thin', color: { argb: 'FF466300' } },
    left: { style: 'thin', color: { argb: 'FF466300' } },
    bottom: { style: 'thin', color: { argb: 'FF466300' } },
    right: { style: 'thin', color: { argb: 'FF466300' } }
};
// Define default alignment for data cells
const DEFAULT_CELL_ALIGNMENT = { vertical: 'middle', horizontal: 'left', wrapText: true, indent: 1 };

// --- ADDED: Define the pattern fill for parent rows ---
const PARENT_ROW_FILL = {
    type: 'pattern',
    pattern: 'mediumGray', // Represents a dotted/shaded pattern
    fgColor: { argb: 'FFE7FFAA' }, // Light green for the pattern color
    bgColor: { argb: 'FFFFFFFF' }  // White background ensures readability
};
// --- END ADDED ---

export class SheetStyleHelper {
    /**
     * Applies all standard sheet styling. Called during save.
     * @param {ExcelJS.Worksheet} worksheet The worksheet object.
     * @param {Sheet} sheet The sheet data model from SheetDataStore.
     */
    static applySheetStyles(worksheet, sheet) {
        console.log(`[StyleHelper] Applying sheet styles for: ${worksheet.name}`);
        if (!worksheet || !sheet || !sheet.columns || !sheet.rows) {
            console.error(`[StyleHelper] Worksheet or Sheet data missing for ${worksheet.name}.`);
            return;
        }

        const columns = sheet.columns;
        const rowIds = Array.from(sheet.rows.keys());
        const numDataRows = rowIds.length;
        const numDataCols = columns.length;

        // --- ADDED: Check if any row has an outline level > 0 ---
        let hasHierarchy = false;
        if (sheet.rows) {
            for (const rowData of sheet.rows.values()) {
                if ((rowData._outlineLevel || 0) > 0) {
                    hasHierarchy = true;
                    break;
                }
            }
        }
        console.log(`[StyleHelper] Sheet ${worksheet.name} has hierarchy: ${hasHierarchy}`);
        // --- END ADDED ---

        // --- Comprehensive Style Clearing ---
        console.log(`[StyleHelper] Clearing existing styles for ${worksheet.name}...`);
        // Iterate through ALL rows currently in the worksheet
        for (let r = 1; r <= worksheet.rowCount; r++) {
            const row = worksheet.getRow(r);
            if (row) {
                // Iterate through ALL cells that have ever had a value/style in this row
                row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    // Clear style and specifically fill for ALL cells initially
                    cell.style = {};
                    cell.fill = undefined; // Explicitly clear fill
                    // We don't touch cell.note here based on previous findings
                });
            }
        }
        console.log(`[StyleHelper] Style clearing complete for ${worksheet.name}.`);
        // --- End Comprehensive Style Clearing ---

        this._applyHeaderStyles(worksheet, numDataCols);
        this._applyRowAndCellStyles(worksheet, sheet, columns, rowIds, hasHierarchy);
        this._applyColumnWidths(worksheet, sheet, columns, rowIds);
        console.log(`[StyleHelper] Sheet styles applied for: ${worksheet.name}`);
    }

    /**
     * Applies formatting to the header row (Row 1).
     * @param {ExcelJS.Worksheet} worksheet The worksheet object.
     * @param {number} numDataCols The number of columns based on the data model.
     */
    static _applyHeaderStyles(worksheet, numDataCols) {
        // console.log('[StyleHelper] Applying header styles...'); // Less verbose logging
        const headerRow = worksheet.getRow(1);
        if (!headerRow) return;

        // Set header row height (Approx 1.85x default of 15 points)
        headerRow.height = 27.75; // Adjusted from 20.5

        // Style ONLY the header cells corresponding to data columns
        for (let colNumber = 1; colNumber <= numDataCols; colNumber++) {
            const cell = headerRow.getCell(colNumber);
            if (cell) {
                // Apply specific header styles
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
                cell.font = { ...HEADER_FONT };
                cell.fill = { ...HEADER_FILL }; // Apply black fill ONLY here
                cell.border = { ...ALL_BORDERS };
            }
        }
        // Note: Cells beyond numDataCols were already cleared in the main function's loop

        // Freeze header row
        worksheet.views = [ { state: 'frozen', ySplit: 1, activeCell: 'A2' } ];
        // console.log('[StyleHelper] Header styles applied.'); // Less verbose logging
    }

    /**
     * Applies formatting to all data rows and cells based on the sheet model.
     * @param {ExcelJS.Worksheet} worksheet The worksheet object.
     * @param {Sheet} sheet The sheet data model.
     * @param {Column[]} columns Array of column definitions.
     * @param {string[]} rowIds Array of row IDs.
     * @param {boolean} hasHierarchy Indicates if any row has an outline level > 0.
     */
    static _applyRowAndCellStyles(worksheet, sheet, columns, rowIds, hasHierarchy) {
        // console.log('[StyleHelper] Applying row and cell styles...'); // Less verbose logging

        rowIds.forEach((rowId, rowIndex) => {
            const excelRowIndex = rowIndex + 2; // Data rows start at Excel row 2
            const row = worksheet.getRow(excelRowIndex);
            const rowData = sheet.rows.get(rowId);

            // Set data row height (Approx 1.35x default of 15 points)
            //row.height = 20.25; // Adjusted from 15.5

            if (!rowData) {
                console.warn(`[StyleHelper] Row data not found for ID: ${rowId} at Excel row ${excelRowIndex}`);
                return;
            }

            // --- ADDED: Determine if this row should get the pattern fill ---
            const currentLevel = rowData._outlineLevel || 0;
            const applyPatternFill = hasHierarchy && currentLevel === 0;
            // --- END ADDED ---

            // Apply styles ONLY to cells within the data columns
            columns.forEach((column, colIndex) => {
                const excelColIndex = colIndex + 1;
                const cell = row.getCell(excelColIndex);
                if (cell) {
                    const cellData = rowData[column.heading];
                    const value = cellData?.value;

                    // --- ADDED: Apply pattern fill if applicable (before other styles) ---
                    if (applyPatternFill) {
                        cell.fill = { ...PARENT_ROW_FILL };
                    }
                    // --- END ADDED ---

                    // Determine base font
                    const baseFont = (colIndex === 0) ? FIRST_COL_FONT : DEFAULT_FONT;

                    // --- MODIFIED: Calculate alignment based on outline level for the first column ---
                    let cellAlignment = { ...DEFAULT_CELL_ALIGNMENT }; // Start with default
                    if (colIndex === 0) {
                        // Indent based on outline level:
                        // Level 0 -> indent 1
                        // Each subsequent level increases indent by 2
                        // Formula: indent = (level * 2) + 1
                        cellAlignment.indent = (currentLevel * 2) + 1;
                        // Set vertical alignment to middle for the first column
                        cellAlignment.vertical = 'middle';
                        // --- ADDED: Enable text wrapping for the first column ---
                        cellAlignment.wrapText = true;
                        // --- END ADDED ---
                    }
                    // --- END MODIFIED ---

                    // Apply base styles (fill is handled above or cleared globally)
                    cell.font = { ...baseFont };
                    cell.border = { ...ALL_BORDERS };
                    // Apply the calculated alignment
                    cell.alignment = cellAlignment; // Use the potentially modified alignment (now defaults to vertical: 'middle')

                    // Apply type-specific formatting
                    // Pass the calculated alignment object
                    CellFormatHelper.applyFormatToCell(cell, column.type.id, value, cellAlignment);

                    // Ensure correct base font if not rich text
                    if (!(cell.value && typeof cell.value === 'object' && cell.value.richText)) {
                        cell.font = { ...baseFont };
                    }
                }
            });
             // Note: Cells beyond the data columns in this row were already cleared globally
        });
        // console.log('[StyleHelper] Row and cell styles applied.'); // Less verbose logging
    }

    /**
     * Calculates and applies column widths based on content.
     * @param {ExcelJS.Worksheet} worksheet The worksheet object.
     * @param {Sheet} sheet The sheet data model.
     * @param {Column[]} columns Array of column definitions.
     * @param {string[]} rowIds Array of row IDs.
     */
    static _applyColumnWidths(worksheet, sheet, columns, rowIds) {
        // console.log('[StyleHelper] Applying column widths...');
        if (!columns || columns.length === 0) return;

        columns.forEach((column, index) => {
            const colNumber = index + 1;
            const wsColumn = worksheet.getColumn(colNumber);

            // Use width stored in our Column model (calculated in SheetDataStore.saveChanges)
            if (column.width) {
                 // Apply the calculated and clamped width
                 wsColumn.width = column.width;
            } else {
                // Fallback if no width is stored (shouldn't happen with the new logic)
                wsColumn.width = 10; // Default width
            }
        });
         // console.log('[StyleHelper] Column widths applied.');
    }

    // _calculateTextWidth can likely be removed if we always use stored widths
} 